import { useMemo } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { ReactComponent as CheckIcon } from "assets/images/iconsV2/check.svg";
import { ReactComponent as XIcon } from "assets/images/iconsV2/x-close.svg";
import { getFreightModeIcon } from "@RequestFormV2/components/ReviewPage/utils/freightModeIcons";
import { getServiceRequirementIcons } from "@RequestFormV2/components/ReviewPage/utils/serviceTypeIcons";
import { useGetFreightModesConfig } from "api/queries/location/useGetFreightModesConfig";
import { useGetScheduleOptions } from "api/queries/location/useGetScheduleOptions";
import { classNames } from "helpers/commonHelpers";

import { DetailsSheetSection, SheetField, StatusWithInstructions } from "../DetailsSheetSection";
import { getDeliveryServiceLevelLabel } from "../getDeliveryServiceLevelLabel";
import { resolveFreightModeForIcon } from "../resolveFreightModeForIcon";
import { resolveServiceRequirementIconKey } from "../resolveServiceRequirementIconKey";
import { SHIPMENT_DETAILS_TEST_IDS, shipmentDetailsTestId } from "../testIds";
import type { ShipmentDetailsSheetViewModel } from "../types";

type CustomsBroker = {
  team_name?: string;
  company?: string;
};

type ServiceSectionProps = {
  service: ShipmentDetailsSheetViewModel["service"];
  isCustomBroker?: boolean;
  winningProvider?: string | null;
  paymentLocationReference?: string;
};

function splitIntoColumns<T>(items: T[], numColumns: number): T[][] {
  const columns: T[][] = Array.from({ length: numColumns }, () => []);
  const perColumn = Math.ceil(items.length / numColumns);
  items.forEach((item, i) => {
    const col = Math.floor(i / perColumn);
    columns[Math.min(col, numColumns - 1)].push(item);
  });
  return columns;
}

const toModeKey = (modeName: string) =>
  modeName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

export function ServiceSection({
  service,
  isCustomBroker,
  winningProvider,
  paymentLocationReference,
}: ServiceSectionProps) {
  const intl = useIntl();

  const { data: scheduleOptionsData } = useGetScheduleOptions(paymentLocationReference ?? "", {
    enabled: !!paymentLocationReference,
  });
  const { data: freightModesConfig } = useGetFreightModesConfig(paymentLocationReference ?? "", {
    enabled: !!paymentLocationReference,
  });
  const availableFreightModes = freightModesConfig?.freight_modes?.available_modes ?? [];
  const showServiceLevel = Boolean(scheduleOptionsData?.schedule_options?.service_levels);

  const freightModes = service.freightModes ?? [];
  const specialRequirements = service.specialRequirements ?? [];

  const deliveryServiceLevel = service.delivery_service_level as string | undefined;
  const deliveryServiceLevelLabel = deliveryServiceLevel
    ? getDeliveryServiceLevelLabel(intl, deliveryServiceLevel)
    : undefined;
  const serviceRequirementDisplay = service.service_requirement_display as string | undefined;
  const serviceRequirementCode = service.service_requirement as string | undefined;
  const serviceType = service.service_type as string | undefined;
  const incoterm = service.incoterm as string | undefined;
  const incotermInstructions = service.incoterm_instructions as string | undefined;
  const insurance = service.insurance as boolean | undefined;
  const insuranceInstructions = service.insurance_instructions as string | undefined;
  const customsBroker = service.customs_broker as CustomsBroker | undefined;

  const freightColumns = useMemo(() => splitIntoColumns(freightModes, 3), [freightModes]);

  const serviceRequirementIconKey = resolveServiceRequirementIconKey(
    serviceRequirementCode,
    serviceType,
    serviceRequirementDisplay,
  );
  const serviceRequirementIcons = serviceRequirementIconKey
    ? getServiceRequirementIcons(serviceRequirementIconKey)
    : null;

  const incotermLabel = incoterm === "NONE" ? "None" : incoterm;

  const customsBrokerValue = customsBroker
    ? `${customsBroker.team_name ?? ""}${customsBroker.company ? ` (${customsBroker.company})` : ""}`.trim() || null
    : null;

  return (
    <DetailsSheetSection
      testId={SHIPMENT_DETAILS_TEST_IDS.SHIPMENT_DETAILS_SECTION_SERVICE}
      title={<FormattedMessage id="quote-request-detail.service" defaultMessage="Service" />}
    >
      <div className="rfv2-review__section-content">
        {isCustomBroker && winningProvider && (
          <SheetField
            testId={SHIPMENT_DETAILS_TEST_IDS.SHIPMENT_DETAILS_SERVICE_WINNING_PROVIDER}
            label={<FormattedMessage id="general.provider" defaultMessage="Provider" />}
            value={winningProvider}
            hideInPrint
          />
        )}

        <SheetField
          testId={SHIPMENT_DETAILS_TEST_IDS.SHIPMENT_DETAILS_SERVICE_CUSTOMS_BROKER}
          label={<FormattedMessage id="general.customsBroker" defaultMessage="Customs broker" />}
          value={customsBrokerValue}
        />

        {freightModes.length > 0 && (
          <div className="rfv2-review__service_field" data-testid={SHIPMENT_DETAILS_TEST_IDS.SHIPMENT_DETAILS_SERVICE_FREIGHT_MODES}>
            <span className="rfv2-review__field-label">
              <FormattedMessage id="general.freightMode" defaultMessage="Freight mode" />
            </span>
            <div className="rfv2-review__freight-columns">
              {freightColumns.map((column, colIdx) => (
                <div
                  key={colIdx}
                  className={classNames("rfv2-review__freight-column", {
                    "rfv2-review__freight-column--middle": colIdx === 1,
                  })}
                >
                  {column.map((modeName) => {
                    const modeKey = toModeKey(modeName);
                    const { value, type } = resolveFreightModeForIcon(modeName, availableFreightModes);
                    const Icon = getFreightModeIcon(value, type);
                    return (
                      <div
                        key={modeName}
                        className={classNames("rfv2-review__freight-item", {
                          "rfv2-review__freight-item--ocean": type === "ocean",
                        })}
                        data-testid={shipmentDetailsTestId.freightMode(modeKey)}
                      >
                        <Icon className="rfv2-review__freight-item__icon" />
                        <span className="rfv2-review__freight-item__label">{modeName}</span>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        )}

        {showServiceLevel && deliveryServiceLevel && (
          <SheetField
            testId={SHIPMENT_DETAILS_TEST_IDS.SHIPMENT_DETAILS_SERVICE_LEVEL}
            label={<FormattedMessage id="quote-request-detail.serviceLevel" defaultMessage="Service level" />}
            value={deliveryServiceLevelLabel}
          />
        )}

        {serviceRequirementDisplay && (
          <div className="rfv2-review__service_field" data-testid={SHIPMENT_DETAILS_TEST_IDS.SHIPMENT_DETAILS_SERVICE_REQUIREMENT}>
            <span className="rfv2-review__field-label">
              <FormattedMessage id="quote-request-detail.serviceRequirement" defaultMessage="Service requirement" />
            </span>
            <span className="rfv2-review__blue-badge">
              {serviceRequirementIcons && (
                <span className="rfv2-review__service-type-icons">
                  <serviceRequirementIcons.origin className="rfv2-review__service-type-icon" />
                  <serviceRequirementIcons.arrow className="rfv2-review__service-type-arrow" />
                  <serviceRequirementIcons.destination className="rfv2-review__service-type-icon" />
                </span>
              )}
              <span className="rfv2-review__blue-badge__label">{serviceRequirementDisplay}</span>
            </span>
          </div>
        )}

        {incoterm && (
          <div className="rfv2-review__service_field" data-testid={SHIPMENT_DETAILS_TEST_IDS.SHIPMENT_DETAILS_SERVICE_INCOTERMS}>
            <span className="rfv2-review__field-label">
              <FormattedMessage id="quote-request-detail.incoterms" defaultMessage="Incoterms" />
            </span>
            <span className="rfv2-review__blue-badge">{incotermLabel}</span>
          </div>
        )}

        {incotermInstructions && (
          <SheetField
            testId={SHIPMENT_DETAILS_TEST_IDS.SHIPMENT_DETAILS_SERVICE_INCOTERM_INSTRUCTIONS}
            label={
              <FormattedMessage
                id="new-request.service.incotermInstructions"
                defaultMessage="Incoterm instructions"
              />
            }
            value={incotermInstructions}
          />
        )}

        {insurance !== undefined && (
          <StatusWithInstructions
            testId={SHIPMENT_DETAILS_TEST_IDS.SHIPMENT_DETAILS_SERVICE_INSURANCE}
            label={<FormattedMessage id="general.insurance" defaultMessage="Insurance" />}
            positive={Boolean(insurance)}
            statusText={
              insurance
                ? intl.formatMessage({ id: "general.yes", defaultMessage: "Yes" })
                : intl.formatMessage({ id: "general.no", defaultMessage: "No" })
            }
            instructions={insurance ? insuranceInstructions : undefined}
            instructionsTestId={SHIPMENT_DETAILS_TEST_IDS.SHIPMENT_DETAILS_SERVICE_INSURANCE_INSTRUCTIONS}
          />
        )}

        {specialRequirements.length > 0 && (
          <div
            className="rfv2-review__service_field"
            data-testid={SHIPMENT_DETAILS_TEST_IDS.SHIPMENT_DETAILS_SERVICE_SPECIAL_REQUIREMENTS}
          >
            <span className="rfv2-review__field-label">
              <FormattedMessage
                id="request-form.service.requirements.label"
                defaultMessage="Requirements"
              />
            </span>
            <div className="rfv2-review__req-columns">
              <div className="rfv2-review__req-column">
                {specialRequirements.map((req) => (
                  <div
                    key={req.key}
                    className="rfv2-review__req-item"
                    data-testid={shipmentDetailsTestId.specialRequirement(req.key)}
                  >
                    {req.value ? (
                      <CheckIcon className={classNames("rfv2-review__req-icon", "rfv2-review__req-icon--check")} />
                    ) : (
                      <XIcon className={classNames("rfv2-review__req-icon", "rfv2-review__req-icon--x")} />
                    )}
                    <div>
                      <span className="rfv2-review__req-text">{req.label}</span>
                      {req.additionalInformation && (
                        <>
                          <br />
                          <span className="rfv2-review__req-comment">Comments: {req.additionalInformation}</span>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </DetailsSheetSection>
  );
}
